import { configureHelpDraftScope } from './help-draft-store.ts';
import { setChatOutboxScope, clearChatOutbox } from './job-chat-outbox.ts';
import { clearPaperDrafts } from './paper-drafts.ts';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { authApi } from '../api/endpoints.ts';
import { setCompanyFormatPrefs } from '../i18n/locale.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import {
  ApiRequestError,
  setExpectedLocationId,
  setLocationChangedHandler,
  setSubscriptionRequiredHandler,
  setUnauthenticatedHandler,
} from '../api/client.ts';
import { clearAllDrafts, resumeDraftWrites, setDraftScope, suspendDraftWrites } from '../state/draft-store.ts';
import {
  clearAllMessageDrafts,
  resumeMessageDraftWrites,
  suspendMessageDraftWrites,
} from '../state/message-draft-store.ts';
import { resetSendOutcome } from '../hooks/use-send-outcome.ts';
import { clearTrialBannerDismissals } from './trial-banner-dismissal.ts';
import { cacheSession, readCachedSession } from './session-cache.ts';
import { clearTenantQueries, isAdminQueryKey } from './admin-query-cache.ts';
import { clearOfflineGetCache, setOfflineCacheScope } from './offline-cache.ts';
import {
  beginOfflinePrivacyReset,
  finishOfflinePrivacyReset,
  offlinePrivacyResetIsCurrent,
  retryOfflinePrivacyReset,
  subscribeOfflinePrivacyReset,
} from './offline-privacy-reset.ts';
import {
  clearAllInspectionDrafts,
  resumeInspectionDraftWrites,
  setInspectionDraftScope,
  suspendInspectionDraftWrites,
} from './inspection-draft-store.ts';
import type { Company, LocationSummary, SessionResponse, SessionUser } from '@shared/types/index.ts';
import type { Permission } from '@shared/constants/index.ts';
import type { PublicDemoState } from '@shared/public-demo.ts';

interface SessionContextValue {
  user: SessionUser | null;
  company: Company | null;
  /** The location this session is working in; null only when unassigned. */
  currentLocation: LocationSummary | null;
  /** Every active location the user may switch into. */
  locations: LocationSummary[];
  /**
   * The currency new money at the current location is written in: the
   * location's own when it keeps one (0114), else the company's. Amounts that
   * already exist are read in their own currency, never in this.
   */
  currencyCode: string;
  /** False on deployments without Stripe configuration: nothing to subscribe to. */
  billingEnforced: boolean;
  /** True on those same deployments: a demo, with a short access window. */
  demoMode: boolean;
  /** Days a demo account is granted at signup. Only meaningful with demoMode. */
  demoExpirationDays: number;
  /**
   * Set only inside the shared public demo: the credits this visitor has left,
   * when their visit ends, and when the company is next rebuilt. Null for
   * every real account — including on a demo DEPLOYMENT, which is a different
   * thing (see `demoMode`).
   */
  publicDemo: PublicDemoState | null;
  /**
   * The operator's email when an admin signed in as this user from /admin,
   * null for an ordinary session. The layout draws a standing banner from it.
   */
  impersonatedBy: string | null;
  /**
   * The deployment requires a second factor and this account has none yet:
   * wherever a sign-in lands, it lands on Settings → Security to add one.
   */
  mfaSetupRequired: boolean;
  isLoading: boolean;
  /**
   * True once the SERVER has vouched for `user` in this page's lifetime — a
   * completed fetch or an explicit sign-in, never the remembered copy that
   * `initialData` restores from local storage.
   *
   * The remembered identity exists so `RequireAuth` can open the app with no
   * network; it must not also be what bounces a visitor OFF the sign-in page,
   * because a remembered user whose cookie is dead would be sent into the app
   * only for the first 401 to send them straight back — the login screen
   * flashing in a cycle. Leaving the sign-in page only on the server's own
   * word is the same rule the partner portal pair follows (see
   * pages/partner/login.tsx).
   */
  sessionConfirmed: boolean;
  can: (permission: Permission) => boolean;
  setSession: (session: SessionResponse) => void;
  clearSession: () => void;
  refresh: () => Promise<void>;
  /**
   * Moves this session into another location, then drops cached tenant reads.
   * Every tenant list is location-scoped; platform-admin reads use a separate
   * cookie and must remain independent of the tenant location.
   */
  switchLocation: (locationId: string) => Promise<void>;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export const SESSION_QUERY_KEY = ['session'] as const;

/**
 * Stable authorization boundary shared by every persistent tenant cache.
 * Keeping this in one helper prevents one offline store from overlooking a
 * role, permission, plan or lock change that another store already fences.
 */
export function offlineAuthorizationFor(user: SessionUser, company: Company): string {
  return JSON.stringify([
    user.role,
    [...new Set(user.permissions)].sort(),
    company.effectivePlan ?? null,
    [...new Set(company.planFeatures ?? [])].sort(),
    company.accessLocked ?? false,
  ]);
}

/** Help drafts share the session's user, branch and authorization boundary. */
export function helpDraftScopeFor(
  user: SessionUser | null,
  company: Company | null,
  locationId: string | null,
  impersonatedBy: string | null,
): string | null {
  if (!user?.id || !company?.id || impersonatedBy || user.role === 'crew') return null;
  return JSON.stringify([user.id, company.id, locationId, offlineAuthorizationFor(user, company)]);
}

/**
 * Quote recovery has a narrower visibility boundary than the general API
 * cache. An unrelated permission grant or plan upgrade must not strand dirty
 * estimating work. Read access and the account lock decide whether the quote
 * data may be restored; losing write access merely prevents its replay.
 */
export function quoteDraftAuthorizationFor(user: SessionUser, company: Company): string {
  return JSON.stringify([user.permissions.includes('quotes:read'), company.accessLocked ?? false]);
}

function effectiveAuthorization(session: SessionResponse): string {
  return offlineAuthorizationFor(session.user, session.company);
}

function offlineScopeIdentity(session: SessionResponse | null): string | null {
  if (!session || session.impersonatedBy) return null;
  return JSON.stringify([
    session.user.id,
    session.company.id,
    session.location?.id ?? null,
    effectiveAuthorization(session),
    quoteDraftAuthorizationFor(session.user, session.company),
  ]);
}

/** Impersonated admin sessions deliberately never persist tenant data. */
function configureOfflineCache(session: SessionResponse | null): void {
  configureHelpDraftScope(
    helpDraftScopeFor(
      session?.user ?? null,
      session?.company ?? null,
      session?.location?.id ?? null,
      session?.impersonatedBy ?? null,
    ),
  );
  // Every session this tab installs is the one it renders, so this is also
  // where the API client learns which location the tab is showing — sent on
  // each write so a switch made in another tab cannot redirect it.
  setExpectedLocationId(session?.location?.id ?? null);
  if (!session || session.impersonatedBy) {
    setOfflineCacheScope(null);
    setInspectionDraftScope(null);
    setChatOutboxScope(null);
    // Quote drafts predate the scoped API cache and are keyed only by quote
    // id. Gate both reads and writes while impersonating so simply viewing a
    // tenant quote cannot leave its customer snapshot on the administrator's
    // browser (or recover an unrelated snapshot with a colliding id).
    suspendDraftWrites();
    setDraftScope(null);
    // Half-typed messages are the same kind of thing on a smaller scale: an
    // administrator looking at a tenant's inbox must not leave a sentence
    // behind on their own browser, nor pick one up.
    suspendMessageDraftWrites();
    return;
  }
  const scope = {
    userId: session.user.id,
    companyId: session.company.id,
    locationId: session.location?.id ?? null,
    authorization: effectiveAuthorization(session),
  };
  setDraftScope(
    JSON.stringify([
      scope.userId,
      scope.companyId,
      scope.locationId,
      quoteDraftAuthorizationFor(session.user, session.company),
    ]),
  );
  resumeDraftWrites();
  resumeMessageDraftWrites();
  setOfflineCacheScope(scope);
  setInspectionDraftScope(scope);
  setChatOutboxScope(scope);
  resumeInspectionDraftWrites();
}

/**
 * After a 409 LOCATION_CHANGED: fetch the session afresh so this tab moves onto
 * the location the session is really in, and return that location's name — or
 * null when it did not land anywhere other than the refused one.
 *
 * `cancelRefetch: true`, unlike the 401/402 handlers: a session fetch already
 * in flight may have been sent before the other tab switched, and joining it
 * would republish the old location and leave the next save refused again. A
 * fresh request is the only one known to postdate the conflict. If even that
 * still reports the refused location (a replica a moment behind), it is asked
 * once more before giving up and letting the caller fall back to "reload".
 */
export async function refreshSessionAfterLocationChange(
  queryClient: QueryClient,
  refusedLocationId: string | null,
): Promise<string | null> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY, exact: true }, { cancelRefetch: true });
    const location = queryClient.getQueryData<SessionResponse | null>(SESSION_QUERY_KEY)?.location ?? null;
    if (location && location.id !== refusedLocationId) return location.name;
  }
  return null;
}

class StaleSessionRequestError extends Error {
  constructor() {
    super('The session changed while it was being refreshed.');
    this.name = 'StaleSessionRequestError';
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const sessionCacheWriteGeneration = useRef(0);
  const renderedSessionCacheWriteGeneration = sessionCacheWriteGeneration.current;
  const offlineScopeInstallGeneration = useRef(0);
  const renderedOfflineScopeInstallGeneration = offlineScopeInstallGeneration.current;
  const completedOfflineScopeInstallGeneration = useRef<number | null>(null);
  const installedOfflineScope = useRef<string | null | undefined>(undefined);
  const sessionRequestSequence = useRef(0);

  const installOfflineScope = useCallback((session: SessionResponse | null) => {
    const generation = ++offlineScopeInstallGeneration.current;
    configureOfflineCache(session);
    installedOfflineScope.current = offlineScopeIdentity(session);
    completedOfflineScopeInstallGeneration.current = generation;
  }, []);

  /**
   * Synchronously fence every tenant-scoped persistent store, then erase it
   * in the background. Forced expiry has the same shared-device privacy
   * boundary as pressing Sign out; treating only the explicit button this way
   * leaves quote snapshots available to the next account.
   */
  const clearProtectedOfflineState = useCallback((broadcast = true): Promise<boolean> => {
    // No earlier render may restore a remembered scope after this fence.
    const fenceGeneration = ++offlineScopeInstallGeneration.current;
    // Publish the persisted pending epoch first. Other tabs synchronously see
    // it from their next store operation even if the browser has not delivered
    // the BroadcastChannel/storage event yet.
    const resetId = broadcast ? beginOfflinePrivacyReset() : null;
    const paperCleared = clearPaperDrafts();
    const apiClear = clearOfflineGetCache();
    suspendDraftWrites();
    setDraftScope(null);
    // Nothing to await: the message drafts only ever existed in memory, so
    // suspending the writes empties them there and then.
    suspendMessageDraftWrites();
    configureHelpDraftScope(null);
    const quoteClear = clearAllDrafts();
    suspendInspectionDraftWrites();
    setInspectionDraftScope(null);
    setChatOutboxScope(null);
    installedOfflineScope.current = null;
    setExpectedLocationId(null);
    completedOfflineScopeInstallGeneration.current = fenceGeneration;
    const inspectionClear = clearAllInspectionDrafts();
    const chatClear = clearChatOutbox();
    const cleared = Promise.all([apiClear, quoteClear, inspectionClear, chatClear])
      .then((results) => paperCleared && results.every((result) => result))
      .catch(() => false);
    if (resetId) {
      void cleared.then((didClear) => {
        if (didClear) finishOfflinePrivacyReset(resetId);
        else retryOfflinePrivacyReset();
      });
    }
    return cleared;
  }, []);

  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: async () => {
      const requestSequence = ++sessionRequestSequence.current;
      const dispatchedScopeGeneration = offlineScopeInstallGeneration.current;
      const session = await authApi.session();
      // Sign-out, location switching and an explicit session replacement all
      // advance this token before publishing. A late old answer may neither
      // reinstall its tenant stores nor enter React Query as current data.
      if (
        requestSequence !== sessionRequestSequence.current ||
        dispatchedScopeGeneration !== offlineScopeInstallGeneration.current
      ) {
        throw new StaleSessionRequestError();
      }
      // Install the authorization fingerprint before React Query can publish
      // this refreshed session to descendants that may immediately fetch.
      installOfflineScope(session);
      return session;
    },
    // A 401 is the normal answer for a signed-out visitor, and a response
    // fenced by a newer local session decision must never be sent again.
    retry: (failureCount, error) =>
      !(error instanceof StaleSessionRequestError) &&
      !(error instanceof ApiRequestError && error.isUnauthenticated) &&
      failureCount < 2,
    staleTime: 5 * 60 * 1000,
    // Run the request even while TanStack knows the browser is offline. The
    // API client can then serve an exact IndexedDB hit after fetch fails;
    // `online` mode would pause before that fallback was ever consulted.
    networkMode: 'always',
    /**
     * Who this browser was last told it is, so the app can open with no
     * network at all.
     *
     * Without it an offline load is not a slow load, it is a sign-out: the
     * query is *paused* rather than pending when `navigator.onLine` is false,
     * so `isLoading` is false and `data` is undefined — which `RequireAuth`
     * reads, correctly by its own logic, as "nobody is signed in" and sends a
     * cleaner mid-walkthrough to a login screen no password can get past
     * without a network.
     *
     * Read once, at mount, which is all the offline case needs.
     */
    initialData: () => readCachedSession() ?? undefined,
    // Stale on arrival, deliberately: this is a remembered answer, not a
    // fetched one, so the real request still goes out the moment there is a
    // network to carry it.
    initialDataUpdatedAt: 0,
  });

  /**
   * Kept in step with whatever the SERVER last said — including the sign-out
   * that sets it to null, and excluding the cache's own answer.
   *
   * That exclusion is the whole of it. `initialData` puts the remembered
   * session into `query.data`, so writing every value back would stamp a new
   * timestamp on a copy nothing had revalidated: open the app offline once a
   * week and a revoked session could stand in for the real one for ever,
   * which is exactly what the expiry exists to prevent. `dataUpdatedAt` is
   * how the two are told apart — `initialDataUpdatedAt` is deliberately 0,
   * and only a real fetch moves it.
   */
  const cached = query.data ?? null;
  const fetchedAt = query.dataUpdatedAt;
  const [offlineScopeReady, setOfflineScopeReady] = useState(false);
  useLayoutEffect(() => {
    const currentGeneration = offlineScopeInstallGeneration.current;
    const cachedScope = offlineScopeIdentity(cached);
    // A queryFn or reset may have installed a newer scope after this render
    // was calculated. Never let the captured remembered identity win it back.
    // If that completed install describes the same boundary, however, release
    // the gate: React Query may structurally reuse the remembered value and
    // therefore never schedule another render for an identical live answer.
    if (renderedOfflineScopeInstallGeneration !== currentGeneration) {
      if (
        completedOfflineScopeInstallGeneration.current === currentGeneration &&
        installedOfflineScope.current === cachedScope
      ) {
        setOfflineScopeReady(true);
      }
      return;
    }
    configureOfflineCache(cached);
    installedOfflineScope.current = cachedScope;
    completedOfflineScopeInstallGeneration.current = currentGeneration;
    // Remembered initialData exists on the first render, before a queryFn can
    // install its scope. Keep descendants gated for that one commit; the
    // layout update is flushed before paint.
    setOfflineScopeReady(true);
  }, [cached, renderedOfflineScopeInstallGeneration]);

  // A sign-out in any open tab has the same privacy boundary in this one:
  // fence writes synchronously, repeat all shared IndexedDB clears, discard
  // the remembered identity and remove in-memory tenant query data.
  useLayoutEffect(
    () =>
      subscribeOfflinePrivacyReset(({ isRetry }) => {
        // Fence passive effects from the render that still held the identity
        // this reset is repairing.
        sessionCacheWriteGeneration.current += 1;
        const cleared = clearProtectedOfflineState(false);
        if (!isRetry) {
          clearTrialBannerDismissals();
          cacheSession(null);
          queryClient.setQueryData(SESSION_QUERY_KEY, null);
          // The session query stays, holding that null. Removed, it would be
          // rebuilt on the next render and sent to the server — and in a tab
          // that is already signed out, the 401 it gets publishes a reset of
          // its own straight back to the tab that sent this one. A password
          // reset ends every session at once, so the tab it was opened in and
          // the tab already open would then flash the sign-in screen and
          // "Loading…" at each other for as long as both stayed open.
          clearTenantQueries(queryClient, SESSION_QUERY_KEY[0]);
          resetSendOutcome();
          return cleared;
        }

        // A failed physical clear is retried while the reset epoch keeps every
        // persistent operation blocked. Do not sign out a session established
        // after the first delivery; restore whichever scope is current only
        // after all three stores have confirmed the retry succeeded.
        return cleared.then((didClear) => {
          if (didClear) {
            const current = queryClient.getQueryData<SessionResponse | null>(SESSION_QUERY_KEY) ?? null;
            installOfflineScope(current);
            cacheSession(current);
          }
          return didClear;
        });
      }),
    [clearProtectedOfflineState, installOfflineScope, queryClient],
  );

  useEffect(() => {
    if (
      renderedSessionCacheWriteGeneration !== sessionCacheWriteGeneration.current ||
      !offlinePrivacyResetIsCurrent()
    ) {
      return;
    }
    if (cached === null || fetchedAt > 0) cacheSession(cached);
  }, [cached, fetchedAt, renderedSessionCacheWriteGeneration]);

  /**
   * The one answer a remembered session must never survive.
   *
   * `/auth/session` is deliberately excluded from the client's global 401
   * handler — a signed-out visitor asking who they are is not a session that
   * just died — and react-query keeps `initialData` when a refetch fails. Put
   * together, an expired or revoked cookie would leave the app rendering the
   * previous user's shell for as long as they left it open, with every other
   * request failing behind it, until something else happened to 401. So this
   * one 401 is read here and answered properly: the session is over.
   *
   * Keyed on the error OBJECT, not on a boolean derived from it: a sign-in
   * after a 401 leaves the old error standing next to the new session (only a
   * fetch outcome replaces it), so a boolean would already be true when the
   * session died a second time and the effect would never fire again — a shell
   * rendering forever around a session that is gone.
   */
  const sessionError = query.error;
  useEffect(() => {
    if (sessionError instanceof ApiRequestError && sessionError.isUnauthenticated) {
      clearTrialBannerDismissals();
      void clearProtectedOfflineState();
      cacheSession(null);
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
      // The session is over as surely as an explicit sign-out, and the send
      // indicator stays mounted on the sign-in screen this lands on — so the
      // queue must not survive into it.
      resetSendOutcome();
    }
  }, [clearProtectedOfflineState, sessionError, queryClient]);

  const setSession = useCallback(
    (session: SessionResponse) => {
      resumeDraftWrites();
      resumeMessageDraftWrites();
      resumeInspectionDraftWrites();
      installOfflineScope(session);
      queryClient.setQueryData(SESSION_QUERY_KEY, session);
    },
    [installOfflineScope, queryClient],
  );

  const clearSession = useCallback(() => {
    clearTrialBannerDismissals();
    // Synchronously fences out old in-flight reads; the small IndexedDB store
    // is removed in the background so sign-out itself never waits on disk.
    void clearProtectedOfflineState();
    // Before the query is touched, not after. Anything that recreates the
    // session query runs `initialData` again, which reads local storage. Left
    // to the effect that keeps the two in step, that read could happen first
    // and restore the user who just signed out, on a device that would then
    // have no network to correct it.
    cacheSession(null);
    queryClient.setQueryData(SESSION_QUERY_KEY, null);
    // Every tenant response and mutation is company-scoped. The platform
    // operator's separate session must survive this tenant sign-out. The
    // session query itself is kept, holding the null just written, so the
    // sign-in screen does not ask the server again (see the reset listener).
    clearTenantQueries(queryClient, SESSION_QUERY_KEY[0]);
    // Outside the query cache entirely (see `use-send-outcome.ts`), so it
    // needs its own clearing here — otherwise the next sign-in in this same
    // tab, or the login page itself, would go on showing the previous
    // customer's email address or phone number. The whole queue, not just the
    // one on screen: a second send finished before the first was dismissed
    // would leave its own notice exposed the moment this one is.
    resetSendOutcome();
  }, [clearProtectedOfflineState, queryClient]);

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
  }, [queryClient]);

  /**
   * Drops every tenant response except the session itself.
   *
   * Removed rather than merely invalidated: an invalidated query keeps serving
   * its old data while it refetches — and keeps it if the refetch fails —
   * which would show one location's customers and visits under another's
   * header, and let someone act on records that are not in scope. Removing
   * forces every screen through its loading state onto the new location's
   * data. The session is exempt: it is what says where we are.
   */
  const purgeScopedQueries = useCallback(() => {
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0] && !isAdminQueryKey(query.queryKey),
    });
    // Location-scoped the same way the removed queries are: a quote emailed
    // at the branch just left has no business surviving as a banner over the
    // one just entered — the whole queue, not just its front, for the same
    // reason `clearSession` above clears all of it.
    resetSendOutcome();
    // And for the same reason, a reply half written to a customer of the
    // branch just left: every other tenant store is scoped to the location,
    // and a draft that outlived one would be the only thing on screen still
    // belonging to somewhere else.
    clearAllMessageDrafts();
  }, [queryClient]);

  const switchLocation = useCallback(
    async (locationId: string) => {
      const result = await authApi.switchLocation(locationId);
      // Write the new location — and the refreshed user — into the cached
      // session first, so the header flips and permission-gated UI updates
      // immediately rather than after the session refetch lands. The user
      // matters as much as the location now: roles can differ per location,
      // and `result.user` carries the role and permissions in force at the
      // new one.
      const current = queryClient.getQueryData<SessionResponse | null>(SESSION_QUERY_KEY) ?? null;
      const next = current
        ? {
            ...current,
            location: result.location,
            locations: result.locations,
            user: result.user ?? current.user,
            company: result.company ?? current.company,
          }
        : null;
      // Fence old-location requests before publishing the new session to the
      // component tree; late responses from the branch just left are ignored.
      installOfflineScope(next);
      queryClient.setQueryData<SessionResponse | null>(SESSION_QUERY_KEY, next);
      // Everything else cached was fetched under the PREVIOUS location's
      // scope. The effect below would catch this too, now that the session
      // holds a different location — but doing it here keeps the purge in the
      // same turn as the switch, so nothing renders in between.
      purgeScopedQueries();
    },
    [installOfflineScope, queryClient, purgeScopedQueries],
  );

  /**
   * The same purge, for a location this app did not choose.
   *
   * The server decides the working location too: a branch archived by a
   * downgrade or by hand, or an assignment an owner revoked, moves the session
   * onto a fallback the next time it is read. Those paths never call
   * `switchLocation`, so without this the header would move while cached
   * customers, quotes and schedules went on showing the branch just left.
   */
  const seenAccessScope = useRef<string | null | undefined>(undefined);
  const sessionAccessScope = query.data
    ? JSON.stringify([
        query.data.user.id,
        query.data.company.id,
        query.data.location?.id ?? null,
        effectiveAuthorization(query.data),
        query.data.impersonatedBy ?? null,
      ])
    : null;
  // A LAYOUT effect, not a passive one: children have already rendered once
  // with the new location's header and permissions against the old location's
  // cached rows, and a passive effect would let the browser paint that frame
  // before the purge lands. This runs — and the re-render it forces runs —
  // before anything reaches the screen.
  useLayoutEffect(() => {
    const previous = seenAccessScope.current;
    seenAccessScope.current = sessionAccessScope;
    // The first resolution has nothing behind it to be stale.
    if (previous === undefined || previous === sessionAccessScope) return;
    purgeScopedQueries();
  }, [sessionAccessScope, purgeScopedQueries]);

  // Any 401 from an ordinary request is a CLAIM that the session ended — one
  // that must be verified before it is believed. Session lookups on read
  // requests may be served by a database replica, so a single request can be
  // refused moments after a sign-in the rest of the app can see; ending the
  // session right here turned that one stray answer into a wiped device and a
  // bounce to /login that reversed on the next request — a browser flashing
  // between the app and the sign-in screen. So the one authority is re-asked
  // instead: if `/auth/session` also answers 401, the effect above ends the
  // session with the full privacy boundary; if it answers with a user, the
  // session stands and the failed request has already shown its own error.
  // The partner portal handles its 401s the same way, and for the same reason
  // (see setPartnerUnauthenticatedHandler in components/partner-layout.tsx).
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      // `cancelRefetch: false`, so a burst of 401s from one dead page JOINS
      // the session refetch already in flight. The default cancels and
      // restarts it — one /auth/session request per failed query, each
      // cancellation postponing the definitive answer that ends the session.
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }, { cancelRefetch: false });
    });
    // A 402 means the trial lock closed while this tab was open — refetch the
    // session so the layout swaps to the lock screen with current state. The
    // same joining, for the same burst shape: a locked page fails many
    // queries at once.
    setSubscriptionRequiredHandler(() => {
      void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }, { cancelRefetch: false });
    });
    // A 409 LOCATION_CHANGED means another tab switched this session's
    // location. A fresh session fetch moves this tab there (the purge above
    // runs once the location differs); the name is handed back only when it
    // really landed somewhere other than the location the refused write
    // claimed, so the message never names a place the page is not.
    setLocationChangedHandler((refusedLocationId) => refreshSessionAfterLocationChange(queryClient, refusedLocationId));
    return () => {
      setUnauthenticatedHandler(null);
      setSubscriptionRequiredHandler(null);
      setLocationChangedHandler(null);
    };
  }, [queryClient]);

  const formatCompany = query.data?.company ?? null;
  // During render, before children format money and dates. An effect would
  // leave the first paint on the browser locale.
  setCompanyFormatPrefs(
    formatCompany
      ? {
          locale: formatCompany.locale ?? null,
          dateFormat: formatCompany.dateFormat ?? null,
          timeFormat: formatCompany.timeFormat ?? null,
        }
      : null,
  );
  useEffect(() => () => setCompanyFormatPrefs(null), []);

  const value = useMemo<SessionContextValue>(() => {
    const session = query.data ?? null;
    return {
      user: session?.user ?? null,
      company: session?.company ?? null,
      currentLocation: session?.location ?? null,
      locations: session?.locations ?? [],
      currencyCode: session?.location?.currencyCode ?? session?.company.currencyCode ?? DEFAULT_CURRENCY_CODE,
      billingEnforced: session?.billingEnforced ?? false,
      demoMode: session?.demoMode ?? false,
      demoExpirationDays: session?.demoExpirationDays ?? 0,
      publicDemo: session?.publicDemo ?? null,
      impersonatedBy: session?.impersonatedBy ?? null,
      mfaSetupRequired: session?.mfaSetupRequired ?? false,
      isLoading: query.isLoading,
      // `initialDataUpdatedAt` is deliberately 0, and only a real fetch or an
      // explicit setQueryData moves it — so this is exactly "not merely
      // remembered". See the note on `cached`/`fetchedAt` above.
      sessionConfirmed: query.dataUpdatedAt > 0,
      can: (permission) => Boolean(session?.user.permissions.includes(permission)),
      setSession,
      clearSession,
      refresh,
      switchLocation,
    };
  }, [query.data, query.isLoading, query.dataUpdatedAt, setSession, clearSession, refresh, switchLocation]);

  return <SessionContext.Provider value={value}>{offlineScopeReady ? children : null}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside a SessionProvider');
  return context;
}
